import { useCallback, useEffect, useMemo, useState } from 'react';

import * as EventEmitter from '@/utils/event-emitter';
import randomString from '@/utils/random-string';

const useFixedDrawer = (params?: { id?: string; closeOnEscape?: boolean; preventDefaultOnEscape?: boolean }) => {
  const { id: propsId, closeOnEscape = true, preventDefaultOnEscape = true } = params || {};

  const id = useMemo(() => {
    return propsId || randomString(7);
  }, [propsId]);

  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);

  const setToggleDrawer = useCallback(() => {
    // emit event to toggle
    const toggleEmmitterId = `${id}::fixed-drawer-toggle`;
    EventEmitter.broadcastEvent(toggleEmmitterId);
  }, [id]);

  const setOpenDrawer = useCallback(() => {
    // emit event to toggle
    const toggleEmmitterId = `${id}::fixed-drawer-open`;
    EventEmitter.broadcastEvent(toggleEmmitterId);
  }, [id]);

  const setCloseDrawer = useCallback(() => {
    // emit event to toggle
    const toggleEmmitterId = `${id}::fixed-drawer-close`;
    EventEmitter.broadcastEvent(toggleEmmitterId);
  }, [id]);

  useEffect(() => {
    const isOpenListenerId = `${id}::fixed-drawer-open-state`;
    const isClosingListenerId = `${id}::fixed-drawer-closing-state`;
    const handleOpenStateChange = (val: boolean) => {
      setOpen(val);
    };
    const handleClosingStateChange = (val: boolean) => {
      setClosing(val);
    };
    EventEmitter.addEventListener(isOpenListenerId, handleOpenStateChange);
    EventEmitter.addEventListener(isClosingListenerId, handleClosingStateChange);
    return () => {
      EventEmitter.removeEventListener(isOpenListenerId, handleOpenStateChange);
      EventEmitter.removeEventListener(isClosingListenerId, handleClosingStateChange);
    };
  }, [id]);

  useEffect(() => {
    if (!open) return;

    const handleKeypress = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open && closeOnEscape) {
        if (preventDefaultOnEscape) {
          e.preventDefault();
        }
        setCloseDrawer();
      }
    };

    document.addEventListener('keydown', handleKeypress);

    return () => {
      document.removeEventListener('keydown', handleKeypress);
    };
  }, [closeOnEscape, open, preventDefaultOnEscape, setCloseDrawer]);

  return useMemo(
    () => ({
      setToggleDrawer,
      setCloseDrawer,
      setOpenDrawer,
      open,
      id,
      closing,
    }),
    [id, open, closing, setCloseDrawer, setOpenDrawer, setToggleDrawer]
  );
};

export default useFixedDrawer;
